import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, TouchableWithoutFeedback, Platform } from 'react-native';
import jsQR from 'jsqr';
import { useTheme, Colors, Shadows, Radius, Spacing, FontSize, FontWeight, Icon, Palette } from '../..';

interface QRScannerProps {
  onScanSuccess: (data: string) => void;
  onScanCancel: () => void;
}

let Camera: any = null;
let useCameraDevice: any = null;
let useCodeScanner: any = null;

if (typeof require === 'function') {
  try {
    const vc = require('react-native-vision-camera');
    Camera = vc.Camera;
    useCameraDevice = vc.useCameraDevice;
    useCodeScanner = vc.useCodeScanner;
  } catch {
    // Not available on Web or when package isn't linked yet
  }
}

export const QRScanner: React.FC<QRScannerProps> = ({ onScanSuccess, onScanCancel }) => {
  const { colors, isDark } = useTheme();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hasCameraPermission, setHasCameraPermission] = useState<boolean | null>(null);
  const animationFrameId = useRef<number | null>(null);

  // ── Web branch: jsQR + getUserMedia ──────────────────────────────────────────
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    let activeStream: MediaStream | null = null;

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        setHasCameraPermission(true);
        activeStream = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute('playsinline', 'true');
          videoRef.current.play().catch((err) => {
            console.error('Video play error:', err);
          });
        }
        tick();
      })
      .catch((err) => {
        console.error('Camera access denied:', err);
        setHasCameraPermission(false);
      });

    return () => {
      if (activeStream) activeStream.getTracks().forEach((t) => t.stop());
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  const tick = () => {
    if (
      videoRef.current &&
      videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA &&
      canvasRef.current
    ) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });
        if (code && code.data) {
          onScanSuccess(code.data);
          return;
        }
      }
    }
    animationFrameId.current = requestAnimationFrame(tick);
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onScanCancel}>
      <TouchableWithoutFeedback onPress={onScanCancel}>
        <View style={styles.overlay}>
          <TouchableWithoutFeedback>
            <View style={[styles.modal, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              
              {/* Header */}
              <View style={styles.header}>
                <View style={styles.titleRow}>
                  <View style={[styles.iconBadge, { backgroundColor: colors.brand }]}>
                    <Icon name="camera" size={20} color={colors.brandText} />
                  </View>
                  <View style={styles.titleTextGroup}>
                    <Text style={[styles.title, { color: colors.text }]}>Scan QR Code</Text>
                    <Text style={[styles.subtitle, { color: colors.subtext }]}>Point camera at transfer QR code</Text>
                  </View>
                </View>
                <TouchableOpacity onPress={onScanCancel} activeOpacity={0.7} style={styles.closeBtn}>
                  <Icon name="close" size={18} color={colors.subtext} />
                </TouchableOpacity>
              </View>

              {/* Viewport Box */}
              <View style={[styles.scannerFrame, { backgroundColor: colors.pill, borderColor: colors.border }]}>
                {Platform.OS === 'web' ? (
                  hasCameraPermission === false ? (
                    <View style={styles.errorBox}>
                      <Icon name="camera" size={32} color={colors.subtext} />
                      <Text style={[styles.errorText, { color: colors.subtext }]}>
                        Camera access denied. Enable permissions in your browser.
                      </Text>
                    </View>
                  ) : (
                    <div style={stylesWeb.wrapperStyle}>
                      <video ref={videoRef} style={stylesWeb.videoStyle} />
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                      <div style={stylesWeb.scannerLine} />
                    </div>
                  )
                ) : (
                  <MobileQRScanner onScanSuccess={onScanSuccess} onScanCancel={onScanCancel} />
                )}
              </View>

              {/* Action Bar */}
              <TouchableOpacity
                style={[styles.cancelBtn, { backgroundColor: colors.surface, borderColor: colors.border }]}
                onPress={onScanCancel}
                activeOpacity={0.8}
              >
                <Text style={[styles.cancelBtnText, { color: colors.text }]}>Cancel</Text>
              </TouchableOpacity>

            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

// ── Mobile Branch ─────────────────────────────────────────────────────────────

function MobileQRScanner({ onScanSuccess, onScanCancel }: QRScannerProps) {
  const { colors } = useTheme();
  const hasScanned = useRef(false);

  const device = useCameraDevice ? useCameraDevice('back') : null;
  const codeScanner = useCodeScanner
    ? useCodeScanner({
        codeTypes: ['qr'],
        onCodeScanned: (codes: any[]) => {
          if (hasScanned.current) return;
          const first = codes[0];
          if (first?.value) {
            hasScanned.current = true;
            onScanSuccess(first.value);
          }
        },
      })
    : null;

  if (!Camera || !device || !codeScanner) {
    return (
      <View style={styles.errorBox}>
        <Icon name="camera" size={32} color={colors.subtext} />
        <Text style={[styles.errorText, { color: colors.subtext }]}>
          {!Camera
            ? 'Camera package unavailable.'
            : 'No back camera found on this device.'}
        </Text>
      </View>
    );
  }

  return (
    <Camera
      style={StyleSheet.absoluteFill}
      device={device}
      isActive={true}
      codeScanner={codeScanner}
    />
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex:            1,
    backgroundColor: 'rgba(0,0,0,0.65)',
    justifyContent:  'center',
    alignItems:      'center',
    zIndex:          1000,
  },
  modal: {
    width:        '90%',
    maxWidth:     420,
    borderRadius: Radius.xl,
    borderWidth:  1,
    padding:      Spacing.lg,
    ...Shadows.card,
  },
  header: {
    flexDirection:  'row',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   Spacing.md,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           Spacing.sm,
  } as any,
  titleTextGroup: {},
  title: {
    fontSize:   FontSize.lg,
    fontWeight: FontWeight.bold,
  },
  subtitle: {
    fontSize:   FontSize.xs,
    fontWeight: FontWeight.medium,
  },
  iconBadge: {
    width:          40,
    height:         40,
    borderRadius:   Radius.full,
    justifyContent: 'center',
    alignItems:     'center',
  },
  closeBtn: {
    padding: Spacing.xs,
  },
  scannerFrame: {
    width:        '100%',
    height:       260,
    borderRadius: Radius.md,
    borderWidth:  1,
    overflow:     'hidden',
    marginBottom: Spacing.md,
    position:     'relative',
  },
  errorBox: {
    flex:           1,
    justifyContent: 'center',
    alignItems:     'center',
    padding:        Spacing.lg,
  },
  errorText: {
    fontSize:   FontSize.xs,
    textAlign:  'center',
    marginTop:  Spacing.sm,
    fontWeight: FontWeight.medium,
  },
  cancelBtn: {
    height:         48,
    borderRadius:   Radius.md,
    borderWidth:    1,
    justifyContent: 'center',
    alignItems:     'center',
  },
  cancelBtnText: {
    fontSize:   FontSize.sm,
    fontWeight: FontWeight.bold,
  },
});

const stylesWeb = {
  wrapperStyle: {
    position: 'relative' as const,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden' as const,
  },
  videoStyle: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  scannerLine: {
    width: '100%',
    height: '2px',
    backgroundColor: Palette.white,
    position: 'absolute' as const,
    boxShadow: `0 0 8px ${Palette.white}`,
    animation: 'scan 2.5s linear infinite',
  },
};
